import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scoreApi, ratingApi, cycleApi } from '../../services/api';
import { Spinner, StatCard, Modal, EmptyState } from '../../components/shared/index';
import {
  AdjustmentsHorizontalIcon, ArrowLeftIcon, UsersIcon, ChartBarIcon,
  ArrowTrendingUpIcon, ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';

function Chip({ tone = 'gray', children }) {
  const tones = {
    gray:  'bg-gray-100 text-gray-700 border-gray-200',
    blue:  'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red:   'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap', tones[tone])}>
      {children}
    </span>
  );
}

function ModifierForm({ row, onClose, onSave, saving }) {
  const current = row.modifier ?? row.score_breakdown?.modifier ?? 0;
  const [mod, setMod]       = useState(Number(current) || 0);
  const [reason, setReason] = useState(row.modifier_reason || '');
  const valid = reason.trim().length >= 50 && mod >= -5 && mod <= 5;

  return (
    <div className="space-y-5">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        A modifier adjusts the blended score by up to ±5 points. Use sparingly and only for factors outside the scoring model. Every change is audited.
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label mb-0">Modifier</label>
          <span className={clsx(
            'font-mono text-lg font-bold',
            mod > 0 ? 'text-green-600' : mod < 0 ? 'text-red-600' : 'text-gray-700'
          )}>
            {mod > 0 ? '+' : ''}{mod.toFixed(1)}
          </span>
        </div>
        <input
          type="range"
          min="-5" max="5" step="0.1"
          value={mod}
          onChange={(e) => setMod(parseFloat(e.target.value))}
          className="w-full accent-blue-600"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>-5.0</span><span>0</span><span>+5.0</span>
        </div>
      </div>

      <div>
        <label className="label">Justification ({reason.length}/50 min)</label>
        <textarea
          className="input h-28 resize-none"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain why this modifier is justified — reference specific behaviours, context, or factors not captured in IP/TP/CP…"
        />
        <p className={clsx('text-xs mt-1', reason.length >= 50 ? 'text-green-600' : 'text-red-500')}>
          {reason.length >= 50 ? 'Minimum met' : `${50 - reason.length} more characters needed`}
        </p>
      </div>

      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button
          disabled={!valid || saving}
          onClick={() => onSave({ modifier: mod, reason })}
          className="btn-primary disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Apply modifier'}
        </button>
      </div>
    </div>
  );
}

export default function ScoringScreen() {
  const { cycleId } = useParams();
  const qc          = useQueryClient();
  const [adjusting, setAdjusting] = useState(null); // the score row being adjusted

  const { data: cycle } = useQuery({
    queryKey: ['cycle', cycleId],
    queryFn:  () => cycleApi.get(cycleId).then(r => r.data),
  });

  const { data: scores, isLoading } = useQuery({
    queryKey: ['scores', cycleId],
    queryFn:  () => scoreApi.byCycle(cycleId).then(r => r.data?.data || r.data || []),
  });

  const { data: ratings } = useQuery({
    queryKey: ['ratings', cycleId],
    queryFn:  () => ratingApi.byCycle(cycleId).then(r => r.data?.data || r.data || []),
  });

  const ratingByEmployee = useMemo(() => {
    const m = {};
    (ratings || []).forEach(r => { m[r.employee_id] = r; });
    return m;
  }, [ratings]);

  const setModifierMut = useMutation({
    mutationFn: ({ ratingId, data }) => ratingApi.setModifier(ratingId, data),
    onSuccess:  () => {
      toast.success('Modifier applied');
      qc.invalidateQueries({ queryKey: ['scores', cycleId] });
      qc.invalidateQueries({ queryKey: ['ratings', cycleId] });
      qc.invalidateQueries({ queryKey: ['calibration-board', cycleId] });
      setAdjusting(null);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  const rows = scores || [];
  const scored = rows.filter(s => typeof s.final_score === 'number');
  const finals = scored.map(s => s.final_score);
  const stats = {
    count:   rows.length,
    avg:     finals.length ? +(finals.reduce((a, b) => a + b, 0) / finals.length).toFixed(2) : null,
    highest: finals.length ? Math.max(...finals) : null,
    lowest:  finals.length ? Math.min(...finals) : null,
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <Link to={`/app/cycles/${cycleId}`} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
            <ArrowLeftIcon className="w-4 h-4" /> Back to cycle
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">
            Scoring{cycle?.name ? ` — ${cycle.name}` : ''}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Review blended IP/TP/CP scores from tool data + reviews, and set justified modifiers before calibration.
          </p>
        </div>
        <Link to={`/app/cycles/${cycleId}/calibration`} className="btn-primary">
          <ChartBarIcon className="w-4 h-4" />
          Open calibration
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Employees scored" value={stats.count} color="blue"  icon={UsersIcon} />
        <StatCard title="Average final"    value={stats.avg ?? '—'}     color="purple" icon={ChartBarIcon} />
        <StatCard title="Highest final"    value={stats.highest ?? '—'} color="green" icon={ArrowTrendingUpIcon} />
        <StatCard title="Lowest final"     value={stats.lowest ?? '—'}  color="red"   icon={ArrowTrendingDownIcon} />
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No scores yet"
          description="This cycle has no computed scores. Trigger scoring from the cycle page once reviews are submitted and tool data has been ingested."
        />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {['Employee', 'Role', 'IP', 'TP', 'CP', 'Modifier', 'Final', 'Sources', ''].map(h => (
                  <th key={h} className="th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {rows.map(row => {
                const emp     = row.employee || {};
                const bd      = row.score_breakdown || {};
                const sources = bd.ip_sources || {};
                const rating  = ratingByEmployee[row.employee_id];
                const mod     = row.modifier ?? bd.modifier ?? 0;
                const hasTool = sources.tool_data === true || sources.tool_data > 0;

                return (
                  <tr key={row.id || row.employee_id} className="tr-hover">
                    <td className="td">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold shrink-0">
                          {emp.name?.[0] || '?'}
                        </div>
                        <div>
                          <Link to={`/app/employees/${row.employee_id}`} className="text-sm font-medium text-gray-900 hover:text-blue-600">
                            {emp.name || '—'}
                          </Link>
                          <p className="text-xs text-gray-400">{emp.department || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="td text-xs capitalize text-gray-600">
                      {emp.role?.replace('_', ' ') || '—'}{emp.band ? ` · ${emp.band}` : ''}
                    </td>
                    <td className="td text-sm font-mono">{row.ip_score?.toFixed?.(1) ?? '—'}</td>
                    <td className="td text-sm font-mono">{row.tp_score?.toFixed?.(1) ?? '—'}</td>
                    <td className="td text-sm font-mono">{row.cp_score?.toFixed?.(1) ?? '—'}</td>
                    <td className="td">
                      <span className={clsx(
                        'text-sm font-mono font-semibold',
                        mod > 0 ? 'text-green-600' : mod < 0 ? 'text-red-600' : 'text-gray-500'
                      )}>
                        {mod > 0 ? '+' : ''}{Number(mod).toFixed(1)}
                      </span>
                    </td>
                    <td className="td">
                      <span className="text-base font-bold text-gray-900">
                        {row.final_score?.toFixed?.(1) ?? '—'}
                      </span>
                    </td>
                    <td className="td">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {typeof sources.manager_reviews === 'number' && (
                          <Chip tone="blue">Mgr reviews: {sources.manager_reviews}</Chip>
                        )}
                        {typeof sources.peer_reviews === 'number' && (
                          <Chip tone="blue">Peer: {sources.peer_reviews}</Chip>
                        )}
                        <Chip tone={hasTool ? 'green' : 'gray'}>
                          Tool data: {hasTool ? '✓' : '✗'}
                        </Chip>
                        {typeof sources.goal_attainment === 'number' && (
                          <Chip tone="amber">Goal: {sources.goal_attainment}%</Chip>
                        )}
                      </div>
                    </td>
                    <td className="td">
                      {rating ? (
                        <button
                          onClick={() => setAdjusting({ ...row, ratingId: rating.id, modifier: mod, modifier_reason: rating.modifier_reason })}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline whitespace-nowrap"
                        >
                          <AdjustmentsHorizontalIcon className="w-4 h-4" />
                          Adjust modifier
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">No rating yet</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!adjusting}
        onClose={() => setAdjusting(null)}
        title={`Adjust modifier — ${adjusting?.employee?.name || ''}`}
        size="md"
      >
        {adjusting && (
          <ModifierForm
            row={adjusting}
            saving={setModifierMut.isPending}
            onClose={() => setAdjusting(null)}
            onSave={(data) => setModifierMut.mutate({ ratingId: adjusting.ratingId, data })}
          />
        )}
      </Modal>
    </div>
  );
}
